/*
 * @Author: Teemo Ding
 * @Date: 2022-04-14 11:17:20
 * @LastEditTime: 2023-03-01 14:20:08
 * @LastEditors: Teemo Ding
 * @FilePath: /unifyxp-v2-store-portal/src/pages/Settings/Permissions/PermissionDetail/index.tsx
 */
import { Skeleton } from '@/src/components/Antd';
import CommonHeader from '@/src/components/CommonHeader';
import RouteBlock from '@/src/constrains/RouterBlock/RouterBlock';
import {
  getEditUser,
  getInviteUser,
  saveUser,
  sendInviteUser,
} from '@/src/request/settings/permission';
import {
  isUserValid,
  storeResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import { IInviteUserType, UserStatus } from '@/src/types/common/permission';
import {
  EPermissionSource,
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
} from '@/src/types/common/resources';
import { useLocalStorageState, useRequest } from 'ahooks';
import { Form, message } from 'antd';
import React, { useState } from 'react';
import { useHistory, useParams } from 'react-router';
import { useI18n } from 'src/hooks/useRouterChange';
import { ACCOUNT_INFO as _ACCOUNT_INFO } from 'src/tools/config';
import { NEW } from 'src/tools/constant';
import { generateKey } from 'src/tools/getUserInfo';
import { i18nGet } from 'src/tools/tools';
import AccountActivities from './AccountActivities';
import Security from './Security';
import UserInformation from './UserInformation';
import UserPermission from './UserPermission';
import { breadcrumbData } from './config';
import css from './style.module.less';
import { useGetAreaCodeText } from 'src/hooks/useGetAreaCodeText';
import { get } from 'lodash';

interface IParams {
  userUuid: string;
}

const permissions = () => {
  const view_settings = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsUserPermission,
        operation: EnumOperation.view_settings,
      }),
    ],
  });
  const add_edit_user = isUserValid({
    source: EPermissionSource.store,
    resourcesGroup: [
      storeResourceNameGenerator({
        category: EnumCategory.Settings,
        subCategory: EnumSubCategory.SettingsUserPermission,
        operation: EnumOperation.add_edit_user,
      }),
    ],
  });
  return {
    view_settings,
    add_edit_user,
  };
};
const PermissionDetail = () => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const { view_settings, add_edit_user } = permissions();
  const history = useHistory();
  const { userUuid } = useParams<IParams>();
  const [form] = Form.useForm();
  const [hasModified, setHasModified] = useState(
    userUuid !== NEW ? false : true,
  );
  const [userInfo, setUserInfo] = useState({
    email: '',
    areaCodeInfo: null,
    fullName: '',
    inviteUserType: IInviteUserType.Email,
  });
  const k = generateKey(_ACCOUNT_INFO);
  const [accountInfo] = useLocalStorageState<any>(k, { storeUuid: '' });
  const readOnly = accountInfo.userUuid === userUuid;
  const { data: {} = {}, loading } = useRequest(
    () => getInviteUser(accountInfo.storeUuid),
    {
      ready: userUuid === NEW && view_settings,
      onSuccess: (res) => {
        const { storeRoles = [], salesChannelRoles = [] } = res || {};
        form.setFieldsValue({ storeRoles, salesChannelRoles });
      },
    },
  );
  const {
    data: {
      fullName = '',
      email,
      mobile,
      areaCodeInfo,
      loginSessions = [],
      status,
      lastInvitationTime,
    } = {},
  } = useRequest(() => getEditUser(accountInfo.storeUuid, userUuid), {
    ready: userUuid && userUuid !== NEW && view_settings,
    onSuccess: (res) => {
      const {
        inviteUserType = IInviteUserType.Email,
        email = '',
        mobile = '',
        areaCodeInfo = null,
        storeRoles = [],
        salesChannelRoles = [],
        fullName = '',
      } = res || {};
      form.setFieldsValue({
        inviteUserType,
        email,
        storeRoles,
        salesChannelRoles,
        mobile: { mobile, areaCodeInfo },
      });
      setUserInfo({
        email: email,
        areaCodeInfo: areaCodeInfo,
        fullName: fullName,
        inviteUserType: inviteUserType,
      });
    },
  });
  const onValuesChange = () => {
    setHasModified(true);
  };

  const { data: {} = {}, run: _saveUser } = useRequest(
    (data) => saveUser(accountInfo.storeUuid, userUuid, data),
    {
      manual: true,
      onSuccess: () => {
        message.success(getI18n('general.savesuccess'));
        setHasModified(false);
      },
    },
  );
  const { data: {} = {}, run: _sendInviteUser } = useRequest(
    (data) => sendInviteUser(accountInfo.storeUuid, data),
    {
      manual: true,
      onSuccess: () => {
        message.success(
          getI18n('usersAndPermissions.detail.invitationsentsuccessfully'),
        );
        onGoBackClick();
      },
    },
  );

  const onGoBackClick = () => {
    history.push({
      pathname: '/settings/permissionsList',
    });
  };
  const handleFinish = (values) => {
    if (userUuid === NEW || status === UserStatus.Invited) {
      const { mobile: { areaCodeInfo = null, mobileNumber = '' } = {} } =
        values;
      values.areaCodeInfo = areaCodeInfo;
      values.mobile = mobileNumber;
      if (status === UserStatus.Invited) {
        _sendInviteUser({ ...values, ...userInfo });
      } else {
        _sendInviteUser(values);
      }
    } else {
      _saveUser(values);
    }
  };
  const areaCodeInText = useGetAreaCodeText(get(areaCodeInfo, 'uuid'));

  return (
    <Skeleton loading={loading} active>
      <RouteBlock
        onConfirm={(f) => f()}
        okText={getI18n('general.discard')}
        scrollToFirstError
        title={getI18n('general.commonHeader.discard_header')}
        content={getI18n('general.commonHeader.discard_content')}
      />
      <CommonHeader
        breadcrumbData={breadcrumbData({ getI18n })}
        onGoBackClick={onGoBackClick}
        onSaveDisable={!hasModified || !add_edit_user}
        onDiscard={onGoBackClick}
        {...(!readOnly && { onSubmit: () => form.submit() })}
        saveText={
          userUuid === NEW
            ? getI18n('usersAndPermissions.detail.sendInvite')
            : getI18n('general.save')
        }
      />
      <Form
        name='permissionDetail'
        onFinish={handleFinish}
        form={form}
        onValuesChange={onValuesChange}
        className={css.commonContainer}>
        <UserInformation
          name={fullName}
          email={email}
          mobile={mobile ? `+${areaCodeInText} ${mobile}` : ''}
          disabled={readOnly || userUuid !== NEW || !add_edit_user}
        />
        <UserPermission
          form={form}
          disabled={readOnly || !add_edit_user}
          onValueChange={onValuesChange}
        />
        {userUuid !== NEW && (
          <AccountActivities
            loginSessions={loginSessions}
            status={status}
            onResend={handleFinish}
            lastInvitationTime={lastInvitationTime}
            form={form}
          />
        )}
        {userUuid !== NEW && <Security disabled={readOnly || !add_edit_user} />}
      </Form>
    </Skeleton>
  );
};

export default PermissionDetail;
